// frontend/src/admin/v2/JobsList.jsx
import { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import {
  Stack, Box, Typography, Alert, AlertTitle, Skeleton, Snackbar, Button,
  useTheme, useMediaQuery,
} from '@mui/material'
import JobCard from './components/JobCard'
import { useJobs } from './hooks/useJobs'
import { SP, FS, TOAST_DURATION } from '../../theme/tokens'
import { formatDuration } from '../../utils/format'
import Panel from '../../components/common/Panel'
import ListToolbar from '../../components/common/ListToolbar'
import FilterPillRow from '../../components/common/FilterPillRow'
import EmptyState from '../../components/common/EmptyState'
import RefreshButton from '../../components/common/RefreshButton'
import useListQuery from '../../components/common/listQuery/useListQuery'
import { etikett } from './adminStatus'

// EINE Quelle statt dreier Kopien — dieselbe Form wie `JobDetail`/`JobCard`.
function fmtDuration(s) {
  return formatDuration(s)
}

const API = process.env.REACT_APP_API_BASE_URL
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY
const HEADERS = ADMIN_KEY ? { 'X-Admin-Key': ADMIN_KEY } : {}

/**
 * Die vier Zustaende der Liste — EINE Benennung, EIN Rang.
 *
 * 🔴 Die Werte sind DISJUNKT und werden clientseitig aus `data.jobs` gezaehlt,
 * NICHT aus `data.summary`: `_serialize` macht einen laufenden Job zu `amber`
 * (`backend/admin_v2/jobs.py:896-903`), `summary.amber` enthaelt die laufenden
 * also mit. Ein Pille-Zaehler aus `summary` waere groesser als die Menge, die
 * die Pille filtert — Pille und Liste gingen auseinander.
 *
 * `RANG` ist zugleich die Sortierung („worst first", P18): der Rang ist die
 * Reihenfolge, es gibt keinen zweiten Begriff von „schlimm".
 */
const ZUSTAENDE = [
  { value: 'failing', status: 'red' },
  { value: 'watch', status: 'amber' },
  { value: 'running', status: 'amber', running: true },
  { value: 'healthy', status: 'green' },
  // 🔴 Das Etikett kommt aus `adminStatus.js`, nicht aus einem Literal hier:
  // sonst stuende die Pille neben einer Karte, die dasselbe anders nennt —
  // genau der Zustand, den W10 beendet (drei Ansichten, drei Woerter).
].map(z => ({ ...z, label: etikett(z.status, { running: z.running }) }))
const RANG = { failing: 0, watch: 1, running: 2, healthy: 3 }

/**
 * zustand — der Listen-Zustand eines Jobs, aus den Serverfeldern abgeleitet.
 *
 * Die Reihenfolge der Zweige IST die Definition: „rot schlaegt laeuft" (ein
 * laufender Job mit rotem Status ist rot), „laeuft schlaegt watch".
 */
function zustand(job) {
  if (job.status === 'red') return 'failing'
  if (job.last_run?.status === 'running') return 'running'
  if (job.status === 'amber') return 'watch'
  return 'healthy'
}

/** Der Text, in dem die Suche sucht — alle sieben Felder aus Plan §2.2. */
function heu(job) {
  return [
    job.label, job.kind, job.category, job.schedule_desc,
    job.description, job.script_path, job.cron_endpoint,
  ].filter(Boolean).join(' ').toLowerCase()
}

/**
 * useJobHistory(key) — fetches up to 24 most-recent runs for the mini strip.
 * Stays independent of the list endpoint so each card loads its own data
 * without bloating the bulk fetch.
 */
function useJobHistory(key) {
  const [runs, setRuns] = useState([])
  useEffect(() => {
    let cancelled = false
    axios.get(`${API}/api/admin/jobs/${key}`, { headers: HEADERS })
      .then(r => { if (!cancelled) setRuns(r.data.history || []) })
      .catch(() => { /* card still renders without strip */ })
    return () => { cancelled = true }
  }, [key])
  return runs
}


function JobCardWithHistory({ job, onRunNow, delay }) {
  const history = useJobHistory(job.key)
  return <JobCard job={job} onRunNow={onRunNow} delay={delay} recentRuns={history} />
}


/**
 * JobsList — /admin/jobs page.
 *
 * @description
 *   Card grid of every entry in `JOB_REGISTRY` (35 at the time of writing),
 *   with the list anatomy of `tabellen-filter-suche.md` §2.1: search and
 *   refresh in Z1, four quick filters in Z2, the active-filter chips in Z3.
 *
 *   Each card lazily fetches its own last 24 runs for the mini-history strip —
 *   kept separate from the list endpoint so the initial page render stays
 *   small (the 35 extra calls are a known finding, `§7 Q3` of the plan).
 *
 *   Categories aren't surfaced as section headers; flat for density.
 *
 *   🔴 **Search and filters run CLIENT-SIDE.** The endpoint returns all 35 jobs
 *   uncapped (`backend/admin_v2/jobs.py:931`), so a client predicate is correct
 *   here; a server parameter would be a second source for the same set.
 *
 *   🔴 **„Run now" stays ON the card.** There is no `RowActionsMenu`: this is a
 *   card list, not a table row, and P19 governs table rows (§2.2 of the plan).
 */
export default function JobsList() {
  const { data, loading, error, errorTitle, refetch, runNow, events, dismissEvent } = useJobs()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // ── Der Listen-Zustand lebt in der ADRESSE (§3.1) ─────────────────────────
  const dimensionen = useMemo(() => [
    { key: 'q', kind: 'single', label: 'Search' },
    {
      key: 'state',
      kind: 'quick',
      label: 'State',
      values: ZUSTAENDE.map(z => ({ value: z.value, label: z.label })),
    },
  ], [])
  const query = useListQuery({ dimensions: dimensionen })
  const { state, set, clearAll, chips } = query
  // 🔴 R16 W-6: nur wenn eine Pille gesetzt ist, gibt es „Clear all filters" —
  // bei reiner Suche ist das die Lage der anderen Zeile (Konzept §8.1).
  const gewaehlt = Array.isArray(state.state) ? state.state : []

  // Show only the most-recent event as a Snackbar so multiple completions
  // don't pile up obscuring the page. Older events stay queued in `events`
  // and surface as the previous one is dismissed.
  const currentEvent = events[0] || null

  const cols = isMobile
    ? 'repeat(1, minmax(0, 1fr))'
    : 'repeat(auto-fill, minmax(320px, 1fr))'

  // ── Ableitungen ───────────────────────────────────────────────────────────
  const basis = useMemo(() => data?.jobs || [], [data])

  const zaehler = useMemo(() => {
    const z = { failing: 0, watch: 0, running: 0, healthy: 0 }
    basis.forEach((job) => { z[zustand(job)] += 1 })
    return z
  }, [basis])

  const gefiltert = useMemo(() => {
    const begriff = (state.q || '').trim().toLowerCase()
    const gewaehlt = Array.isArray(state.state) ? state.state : []
    return basis
      .map((job, i) => ({ job, i }))
      .filter(({ job }) => gewaehlt.length === 0 || gewaehlt.includes(zustand(job)))
      .filter(({ job }) => !begriff || heu(job).includes(begriff))
      .sort((a, b) => RANG[zustand(a.job)] - RANG[zustand(b.job)] || a.i - b.i)
      // Stabil: der Rang entscheidet, die Registry-Reihenfolge bleibt der
      // Tiebreaker (P18: „Sorted by state — worst first.").
      .map(({ job }) => job)
  }, [basis, state.q, state.state])

  // 🔴 Der gescheiterte ERSTE Aufbau: es gibt keine Daten, an denen ein Filter
  // etwas einengen koennte. Suche und Pillen sind dann gesperrt (N8.5), und der
  // Grund steht als Leerzustand im Panel — nicht zusaetzlich als Alert
  // darueber (G13a-2: ein Ausfall, eine Meldung).
  const sperre = Boolean(error) && !data
  const leerGrund = sperre
    ? 'error'
    : (data && basis.length === 0)
      ? 'no-data'
      : (data && gefiltert.length === 0)
        ? 'no-match'
        : null

  const gesamt = data?.summary?.total ?? null

  const quickReihe = (
    <Box>
      <FilterPillRow
        alwaysShow
        multi
        label="State"
        options={ZUSTAENDE.map(z => ({ ...z, count: data ? zaehler[z.value] : undefined }))}
        value={state.state ?? []}
        onChange={(v) => set('state', v)}
        disabled={!data}
      />
    </Box>
  )

  const leerzustand = leerGrund ? (
    <Box sx={{ py: SP.lg }}>
      <EmptyState
        reason={leerGrund}
        message={
          leerGrund === 'error'
            ? 'Could not load the job list.'
            : leerGrund === 'no-data'
              // 🔴 R16 W-8/§8.1: ein Satz, WANN Eintraege entstehen.
              ? 'No jobs registered. Jobs appear here once they are declared in the job registry.'
              : undefined
        }
        noun={leerGrund === 'no-match' ? 'job' : undefined}
        query={leerGrund === 'no-match' ? (state.q || undefined) : undefined}
        onClearSearch={
          leerGrund === 'no-match' && state.q ? () => set('q', null) : undefined
        }
        // 🔴 R16 W-10 (2026-11-01): derselbe Knopf wie in E2/E3 — im `action`-Slot,
        // NICHT als eingebaute Standardhandlung. `EmptyState` argumentiert selbst
        // dagegen, dass dieselbe Beschriftung zwei Erscheinungen hat: unter 600 px
        // wird der umrandete Knopf zur Glas-Pille, der eingebaute Textknopf nicht
        // (`EmptyState.jsx:288-296`).
        action={
          leerGrund === 'no-match' && gewaehlt.length > 0
            ? <Button variant="outlined" onClick={clearAll}>Clear all filters</Button>
            : undefined
        }
        onRetry={leerGrund === 'error' ? refetch : undefined}
        // 🔴 R16 W-2/F-3: der Env-Hinweis (403) gehoert in den Leerzustand —
        // als ZWEITE Detailzeile unter dem Fehlertext, nicht nur in den
        // Nachlade-Alert. Ohne ihn liest der Betreiber „status code 500" und
        // nicht „REACT_APP_ADMIN_KEY".
        detail={
          leerGrund === 'error'
            ? (
              <>
                {error}
                {errorTitle && <><br />{errorTitle}</>}
              </>
            )
            : undefined
        }
      />
    </Box>
  ) : null

  return (
    <Stack spacing={SP.section}>
      {/* Ein gescheitertes NACH-Laden, waehrend Zeilen stehen: die einzige
          Meldung, die der Leerzustand nicht abdeckt (G13a-2). */}
      {error && !sperre && (
        <Alert severity="error" variant="outlined" title={errorTitle || undefined}>
          {error}
        </Alert>
      )}

      <Panel pad="card" data-testid="liste-panel" aria-busy={loading || undefined}>
        <ListToolbar
          search={{
            value: state.q ?? '',
            onChange: (v) => set('q', v),
            placeholder: 'Search jobs…',
            label: 'Search jobs',
            inputRole: 'searchbox',
            // N8.5: gesperrt, solange es keine Daten gibt — nach dem ersten
            // Aufbau bleibt die Leiste bedienbar (N4.5).
            disabled: !data,
            sx: { flex: 1, minWidth: 0 },
          }}
          quickFilters={quickReihe}
          // 🔴 R16 F-1/W-1: der Treffer-Zaehler bleibt in `no-data` und
          // `no-match` stehen (Hausform `ListenMuster.jsx:705`, P28: „auch der
          // Treffer-Zähler"). NUR der Fehler hat keinen — dort gibt es keine
          // Menge, über die er etwas sagen koennte.
          count={leerGrund === 'error' ? undefined : {
            noun: 'jobs',
            n: data ? gefiltert.length : null,
            m: gesamt,
          }}
          refresh={(
            <RefreshButton
              ariaLabel="Refresh jobs"
              loading={loading}
              firstLoad={!data}
              onClick={refetch}
            />
          )}
          query={{ chips, clearAll }}
        >
          {query.ignoredValues.length > 0 && (
            <Alert severity="info" data-testid="ignoriert-hinweis" sx={{ mt: SP.tight }}>
              {`Ignored unknown filter value “${query.ignoredValues[0].value}”.`}
            </Alert>
          )}

          {/* P18: es gibt keinen Tabellenkopf und keine Sortierachse in der
              Adresse — der Satz ist der EINZIGE Traeger der Sortierung. Er
              steht nur mit Zeilen; ueber einem Leerzustand waere er eine
              Aussage ueber eine Liste, die es nicht gibt. */}
          {!leerGrund && (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid="sortiert-nach"
              sx={{ display: 'block', mt: SP.tight }}
            >
              Sorted by state — worst first.
            </Typography>
          )}

          {loading && !data ? (
            <Box
              data-testid="liste-skelett"
              sx={{ display: 'grid', gridTemplateColumns: cols, gap: SP.card, mt: SP.tight }}
            >
              {[0, 1, 2, 3, 4, 5].map(i => <Skeleton key={i} variant="rounded" height={220} />)}
            </Box>
          ) : leerGrund ? (
            leerzustand
          ) : (
            // `alignItems: 'stretch'` ist die Vorgabe, steht hier aber
            // ausdruecklich: die Karten fuellen ihre Rasterzelle ueber
            // `height: '100%'` (JobCard), und wer hier je etwas anderes setzt,
            // hebt genau das auf.
            <Box
              data-testid="liste-karte"
              sx={{
                display: 'grid', gridTemplateColumns: cols, alignItems: 'stretch',
                gap: SP.card, mt: SP.tight,
              }}
            >
              {gefiltert.map((job, i) => (
                <JobCardWithHistory
                  key={job.key}
                  job={job}
                  onRunNow={runNow}
                  delay={i * 30}
                />
              ))}
            </Box>
          )}
        </ListToolbar>
      </Panel>

      {/* Toast on Run-now-triggered job completion. Only fires for jobs the
          user clicked Run-now on (sessionStorage-tracked) — scheduled cron
          fires never spam toasts. Failure toasts persist longer so the
          error tail is readable. */}
      {/* 🔴 AUSNAHME von der sonner-Hausform (K-F3, 2026-10-09), und zwar die
          EINZIGE verbliebene `Snackbar` in `frontend/src`: dieser Hinweis steht
          in einem FESTEN Bildschirmwinkel (oben rechts, wo das Cockpit seine
          uebrigen Statusflaechen traegt) und traegt STRUKTURIERTEN Inhalt —
          Titelzeile plus Dauer bzw. Fehlerrest in zwei Zeilen. Ein sonner-Toast
          ist eine Zeile Text am unteren Rand; ihn hierher zu ziehen hiesse,
          entweder den Inhalt zu verkuerzen oder zwei Toene in einer Flaeche zu
          mischen. Wer sie dennoch umstellt, aendert Bedienung und Inhalt, nicht
          nur die Huelle — und `duration` nimmt die ZWEI Hauswerte, keine
          eigenen Zahlen. */}
      <Snackbar
        open={Boolean(currentEvent)}
        autoHideDuration={
          // 🔴 R15 W6 (2026-10-09): der Erfolgsfall trug bis hier die
          // sechssekundige Zahl, dann `TOAST_DURATION.short` (4000). Gemessen am
          // 2026-10-09 an `backend/admin_v2/jobs.py`: die Erfolgszeile ist
          // `${label} finished`, und 3 der 32 Bezeichnungen sind laenger als
          // 61 Zeichen (die drei LSM-Portal-Eintraege; laengste 69 → 78 Zeichen
          // Satz). Fuer sie ist die kurze Dauer nicht belegt, sie nehmen `long`;
          // die uebrigen 29 bleiben bei `short`. Die Grenze `61` ist die
          // ~70-Zeichen-Grenze minus der neun Zeichen von „ finished".
          // Der Fehlerfall ist ohnehin `long` — der Fehlerrest will gelesen
          // werden. Die Wahl steht seit R16 W7 als reine Funktion `toastDauer`
          // am Dateiende — pruefbar ohne Montage dieser Seite.
          toastDauer(currentEvent)
        }
        onClose={(_e, reason) => {
          // Allow click-to-dismiss but not auto-dismiss on hover/focus
          if (reason === 'clickaway') return
          if (currentEvent) dismissEvent(currentEvent.id)
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {currentEvent ? (
          <Alert
            severity={currentEvent.status === 'done' ? 'success' : 'error'}
            variant="filled"
            onClose={() => dismissEvent(currentEvent.id)}
            sx={{ minWidth: 320, maxWidth: 480 }}
          >
            <AlertTitle sx={{ fontSize: FS.sm, fontWeight: 600, mb: 0.25 }}>
              {currentEvent.status === 'done'
                ? `${currentEvent.label} finished`
                : `${currentEvent.label} failed`}
            </AlertTitle>
            <Box sx={{ fontSize: FS.xs }}>
              {currentEvent.status === 'done'
                ? `Completed in ${fmtDuration(currentEvent.durationS)}.`
                : (currentEvent.errorTail || 'Check the job detail page for the full error.')}
            </Box>
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </Stack>
  )
}

/**
 * Die Dauer des Lauf-jetzt-Hinweises — eine REINE Funktion, damit die Grenze
 * pruefbar ist, ohne diese Seite zu montieren.
 *
 * 🔴 R16 W7 (2026-10-09): die Verzweigung war die EINZIGE neue des Zweigs ohne
 * Probe. `JobsList` haengt an `useJobs` (Netz), am Theme und an `JobCard`; eine
 * Montage pruefte viel und die Grenze nur mittelbar. Hier steht sie allein —
 * die Probe daneben (`JobsList.dauer.test.jsx`) pinnt genau die Zahl 61.
 *
 * Der Fehlerfall ist IMMER `long` (der Fehlerrest will gelesen werden). Im
 * Erfolgsfall entscheidet die Laenge des Bezeichners, weil die gezeigte Zeile
 * `${label} finished` ist und lange LSM-Portal-Namen sonst zu schnell
 * verschwinden. Die Grenze `61` ist die ~70-Zeichen-Grenze minus der neun
 * Zeichen von „ finished" — gemessen am 2026-10-09 an
 * `backend/admin_v2/jobs.py`: 32 Bezeichnungen, genau 3 ueber 61, laengste 69.
 *
 * @param {{ status?: string, label?: string } | null} event  der juengste Lauf
 * @returns {number} `TOAST_DURATION.long` oder `TOAST_DURATION.short`
 */
export function toastDauer(event) {
  return event?.status === 'error' || (event?.label?.length ?? 0) > 61
    ? TOAST_DURATION.long
    : TOAST_DURATION.short
}
